import { useState } from 'react';
import Box from '@mui/material/Box';
import {
  Dialog,
  RadioGroupControl,
  TextV2,
} from '@insights-ltd/design-library/components';
import { Trans, useTranslation } from 'react-i18next';

export type DistributionMethodDialogProps = {
  open: boolean;
  profileCount: number;
  onSendViaLink: (format: string) => void;
  onSendViaEmail: () => void;
  onCancel: () => void;
  showDigitalProfileLink: boolean;
};

const DistributionMethodDialog = ({
  open,
  onSendViaLink,
  onSendViaEmail,
  onCancel,
  profileCount,
  showDigitalProfileLink,
}: DistributionMethodDialogProps) => {
  const { t } = useTranslation();
  const sortedDistributionMethods = [
    ...(showDigitalProfileLink
      ? [
          {
            value: 'DIGITAL_PROFILE_LINK',
            label:
              'ui.event-management.distribute-profiles.label.share-digital-profile-link',
            description:
              'ui.event-management.distribute-profiles.share-digital-profile-link.description',
            format: 'share-digital-profile-link',
            onSubmitFn: onSendViaLink as (format: string) => void,
          },
        ]
      : []),
    {
      value: 'EMAIL',
      label: 'ui.event-management.distribute-profiles.label.email',
      description: 'ui.event-management.distribute-profiles.email.description',
      format: '',
      onSubmitFn: onSendViaEmail as () => void,
    },
    {
      value: 'LINK',
      label: 'ui.event-management.distribute-profiles.label.link',
      description: 'ui.event-management.distribute-profiles.link.description',
      format: 'link',
      onSubmitFn: onSendViaLink as (format: string) => void,
    },
  ];
  const [distribution, setDistribution] = useState(
    sortedDistributionMethods[0].value,
  );

  const [previousOpen, setPreviousOpen] = useState(open);

  const defaultToLinkOnOpen = (newValue: boolean) => {
    if (newValue) {
      setDistribution(sortedDistributionMethods[0].value);
    }
  };

  if (open !== previousOpen) {
    defaultToLinkOnOpen(open);
    setPreviousOpen(open);
  }

  const onSubmit = () => {
    const chosenDistributionMethod = sortedDistributionMethods.filter(
      (distributionMethod) => distributionMethod.value === distribution,
    );

    chosenDistributionMethod[0].onSubmitFn(chosenDistributionMethod[0].format);
  };

  const handleOnClose = () => {
    onCancel();
  };

  return (
    <Dialog
      onSubmit={onSubmit}
      onClose={handleOnClose}
      open={open}
      content={
        <>
          <Box>
            <TextV2 component="span">
              <Trans
                i18nKey="ui.event-management.distribute-profiles.message.bold"
                components={{
                  bold: <TextV2 component="span" variant="bodyBold" />,
                }}
                values={{ count: profileCount }}
              />
            </TextV2>
          </Box>
          <RadioGroupControl
            options={sortedDistributionMethods.map((distributionMethod) => ({
              value: distributionMethod.value,
              description: t(distributionMethod.description),
              label: (
                <strong>
                  {t(distributionMethod.label, {
                    count: profileCount,
                  })}
                </strong>
              ) as unknown as string,
            }))}
            name="roles"
            title={t('ui.event-management.users.create.permission-roles')}
            value={distribution}
            onChange={setDistribution}
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightBold,
            })}
          />
        </>
      }
      primaryButtonText={t(
        'ui.event-management.events.download-profiles.distribute',
      )}
      disablePrimaryButton={distribution === ''}
      secondaryButtonText={t(
        'ui.event-management.distribute-profiles.cancel.button',
      )}
      title={t('ui.event-management.distribute-profiles.title')}
    />
  );
};

export default DistributionMethodDialog;
